import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import NotFoundBlock from "@/components/base/NotFoundBlock";
import ShareModal from "@/components/base/ShareModal";
import usePageMeta from "@/hooks/usePageMeta";
import { postItems } from "@/mocks/content";
import { formatDateTime } from "@/lib/format";

const LIKE_KEY = "hongtao-post-likes";

function readLikes(): string[] {
  try {
    const raw = localStorage.getItem(LIKE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export default function PostDetailPage() {
  const { id } = useParams();
  const post = postItems.find((item) => item.id === id);
  const [likedIds, setLikedIds] = useState<string[]>(readLikes);
  const [shareOpen, setShareOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [commentNotice, setCommentNotice] = useState("");

  usePageMeta(
    post ? `${post.title} - 互动专区 - 红桃网` : "帖子不存在 - 红桃网",
    post ? `${post.excerpt.slice(0, 70)} 红桃网互动专区，影迷讨论与观影分享。` : "你访问的帖子不存在，请返回互动专区继续浏览。",
    post ? `${post.tags.join(",")},互动专区,红桃网` : "互动专区,红桃网",
  );

  const related = useMemo(() => {
    if (!post) return [];
    const sameTag = postItems.filter((item) => item.id !== post.id && item.tags.some((tag) => post.tags.includes(tag)));
    const others = postItems.filter((item) => item.id !== post.id && !item.tags.some((tag) => post.tags.includes(tag)));
    return [...sameTag, ...others].slice(0, 3);
  }, [post]);

  if (!post) {
    return <NotFoundBlock title="帖子不存在" backTo="/community" backLabel="返回互动专区" />;
  }

  const liked = likedIds.includes(post.id);

  const toggleLike = () => {
    setLikedIds((prev) => {
      const next = prev.includes(post.id) ? prev.filter((item) => item !== post.id) : [...prev, post.id];
      try {
        localStorage.setItem(LIKE_KEY, JSON.stringify(next));
      } catch {
        // 忽略本地存储异常
      }
      return next;
    });
  };

  return (
    <>
      <div className="mx-auto w-full max-w-portal px-4 py-5 md:px-6 md:py-7">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "互动专区", to: "/community" },
            { label: "帖子详情" },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <article className="min-w-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-primary-50 px-2.5 py-1 text-[11px] text-primary-700">
                    #{tag}
                  </span>
                ))}
                <span className="ml-auto text-[11px] text-foreground-400">{formatDateTime(post.createdAt)}</span>
              </div>

              <h1 className="mt-3 font-heading text-[24px] font-black leading-snug text-foreground-950 md:text-[30px]">
                {post.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 font-heading text-sm font-bold text-primary-600">
                    {post.author.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-[13px] font-medium text-foreground-900">{post.author}</p>
                    <p className="text-[11px] text-foreground-400">{post.authorTag} · 演示用户</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line" />
                  分享帖子
                </button>
              </div>

              <div className="mt-5 space-y-4 text-[15px] leading-[1.95] text-foreground-800">
                {post.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
                <button
                  type="button"
                  onClick={toggleLike}
                  aria-pressed={liked}
                  className={[
                    "flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors",
                    liked
                      ? "border-primary-400 bg-primary-50 text-primary-600"
                      : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600",
                  ].join(" ")}
                >
                  <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {liked ? "已点赞" : "点赞"}
                  <span className="font-label text-xs">{post.likes + (liked ? 1 : 0)}</span>
                </button>
                <span className="flex items-center gap-1.5 text-xs text-foreground-400">
                  <i className="ri-chat-3-line" />
                  {post.replies} 条讨论
                </span>
                <span className="ml-auto text-[11px] text-foreground-400">点赞状态保存在本机浏览器（演示）</span>
              </div>
            </div>

            <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-chat-3-line text-primary-500" />
                参与讨论
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                评论区需要登录后使用，本站暂未接入账号系统与后端存储。以下输入框为界面演示，不会提交或保存任何内容。
              </p>
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value.slice(0, 500))}
                rows={3}
                maxLength={500}
                placeholder="说说你的看法……"
                className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
              />
              <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span className="font-label text-[11px] text-foreground-400">{comment.length} / 500</span>
                <button
                  type="button"
                  onClick={() =>
                    setCommentNotice("演示状态：评论功能未接入登录与后端，你的内容不会被提交或公开。")
                  }
                  className="cursor-pointer whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
                >
                  登录后回复（演示）
                </button>
              </div>
              {commentNotice && (
                <div className="mt-3 rounded-md bg-accent-50 px-3 py-2.5 text-xs leading-relaxed text-accent-900">
                  <i className="ri-information-line mr-1" />
                  {commentNotice}
                </div>
              )}
            </section>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本帖为虚构演示内容，用户昵称、点赞与回复数均为示例数据。
              </p>
              <Link
                to="/community"
                className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回互动专区
              </Link>
            </div>

            <section className="mt-9">
              <div className="mb-4 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  更多讨论
                </h2>
                <Link to="/community" className="flex items-center gap-0.5 text-xs text-foreground-500 hover:text-primary-600">
                  全部帖子
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/community/${item.id}`}
                    className="group block rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <p className="font-heading text-sm font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground-400">
                      <span>{item.author}</span>
                      <span className="text-foreground-200">·</span>
                      <span>{formatDateTime(item.createdAt)}</span>
                      <span className="text-foreground-200">·</span>
                      <span>{item.replies} 条讨论</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </article>

          <aside className="space-y-4 self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-user-star-line text-primary-500" />
                关于作者
              </h2>
              <div className="mt-3 flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 font-heading text-base font-bold text-primary-600">
                  {post.author.slice(0, 1)}
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{post.author}</p>
                  <p className="text-[11px] text-foreground-400">{post.authorTag}</p>
                </div>
              </div>
              <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                演示账号。在真实的社区中，这里会展示作者的发帖记录与关注入口。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                <i className="ri-hashtag text-primary-500" />
                相关话题
              </h2>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {post.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-primary-950 p-4 text-background-100">
              <p className="font-label text-[11px] tracking-[0.22em] text-accent-400">社区提示</p>
              <p className="mt-2 text-sm font-semibold text-background-50">文明讨论，友善表达</p>
              <p className="mt-1.5 text-xs leading-relaxed text-primary-100/80">
                请勿在讨论中发布真实个人信息。本站为演示项目，不对用户生成内容的真实性负责。
              </p>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={post.title} />
    </>
  );
}